import {defineStore} from 'pinia';

const STORAGE_KEY = 'onboarding';

// Onboarding state is per user. It used to live under one shared key that
// logout deleted outright, so an admin who chose Skip for now was sent back
// into the wizard on the next sign in. Keeping the shared key instead would
// hand one user's progress to the next person signing in on the same browser.
let activeKey = STORAGE_KEY;

function keyFor(userId) {
    return userId ? `${STORAGE_KEY}:${userId}` : STORAGE_KEY;
}

function readKey(key) {
    try {
        const saved = localStorage.getItem(key);
        return saved ? JSON.parse(saved) : {};
    } catch {
        return {};
    }
}

// The store is built before login, so pick the key up from the persisted auth
// blob when there is one. authStore hydrates from the same entry on reload.
function initialKey() {
    try {
        const raw = localStorage.getItem('auth');
        if (!raw) return STORAGE_KEY;
        return keyFor(JSON.parse(raw)?.user?.id);
    } catch {
        return STORAGE_KEY;
    }
}

export const useOnboardingStore = defineStore('onboardingStore', {
    state: () => {
        activeKey = initialKey();
        const data = readKey(activeKey);
        return {
            hasCompletedSetup: data.hasCompletedSetup ?? false,
            checklistDismissed: data.checklistDismissed ?? false,
            // Set when the user leaves the wizard without finishing. Persisted,
            // because the router sends every company admin with 0 devices to the
            // wizard - without this they are returned to it on the next
            // navigation and cannot use the app without adding a card.
            wizardDismissed: data.wizardDismissed ?? false,
            hasActivatedDevice: data.hasActivatedDevice ?? false,
            hasCreatedAsset: data.hasCreatedAsset ?? false,
            hasViewedLiveMap: data.hasViewedLiveMap ?? false,
            hasViewedTrips: data.hasViewedTrips ?? false,
            hasAddedDriver: data.hasAddedDriver ?? false,
            hasAddedLocation: data.hasAddedLocation ?? false,
            // Wizard state (persisted for refresh resilience)
            wizardStep: data.wizardStep ?? null,
            wizardDevice: data.wizardDevice ?? null,
            wizardAssetType: data.wizardAssetType ?? null,
            // Transient state (not persisted)
            _deviceCheckDone: false,
            _deviceCount: null,
        };
    },

    getters: {
        showChecklist: (state) => state.hasCompletedSetup && !state.checklistDismissed && !state.isAllComplete,
        completedMilestones: (state) => [
            state.hasActivatedDevice,
            state.hasCreatedAsset,
            state.hasAddedDriver,
            state.hasAddedLocation,
            state.hasViewedLiveMap,
            state.hasViewedTrips,
        ].filter(Boolean).length,
        totalMilestones: () => 6,
        isAllComplete: (state) => [
            state.hasActivatedDevice,
            state.hasCreatedAsset,
            state.hasAddedDriver,
            state.hasAddedLocation,
            state.hasViewedLiveMap,
            state.hasViewedTrips,
        ].every(Boolean),
        progressPercent: (state) => {
            const completed = [
                state.hasActivatedDevice,
                state.hasCreatedAsset,
                state.hasAddedDriver,
                state.hasAddedLocation,
                state.hasViewedLiveMap,
                state.hasViewedTrips,
            ].filter(Boolean).length;
            return Math.round((completed / 6) * 100);
        },
    },

    actions: {
        _persist() {
            localStorage.setItem(activeKey, JSON.stringify({
                hasCompletedSetup: this.hasCompletedSetup,
                checklistDismissed: this.checklistDismissed,
                wizardDismissed: this.wizardDismissed,
                hasActivatedDevice: this.hasActivatedDevice,
                hasCreatedAsset: this.hasCreatedAsset,
                hasViewedLiveMap: this.hasViewedLiveMap,
                hasViewedTrips: this.hasViewedTrips,
                hasAddedDriver: this.hasAddedDriver,
                hasAddedLocation: this.hasAddedLocation,
                wizardStep: this.wizardStep,
                wizardDevice: this.wizardDevice,
                wizardAssetType: this.wizardAssetType,
            }));
        },

        loadForUser(userId) {
            const key = keyFor(userId);
            let data = readKey(key);

            // Carry the pre-scoping entry over to the first user who signs in
            // after this ships, rather than making them redo the wizard.
            if (userId && !localStorage.getItem(key)) {
                const legacy = localStorage.getItem(STORAGE_KEY);
                if (legacy) {
                    data = readKey(STORAGE_KEY);
                    localStorage.setItem(key, legacy);
                    localStorage.removeItem(STORAGE_KEY);
                }
            }

            activeKey = key;
            this.hasCompletedSetup = data.hasCompletedSetup ?? false;
            this.checklistDismissed = data.checklistDismissed ?? false;
            this.wizardDismissed = data.wizardDismissed ?? false;
            this.hasActivatedDevice = data.hasActivatedDevice ?? false;
            this.hasCreatedAsset = data.hasCreatedAsset ?? false;
            this.hasViewedLiveMap = data.hasViewedLiveMap ?? false;
            this.hasViewedTrips = data.hasViewedTrips ?? false;
            this.hasAddedDriver = data.hasAddedDriver ?? false;
            this.hasAddedLocation = data.hasAddedLocation ?? false;
            this.wizardStep = data.wizardStep ?? null;
            this.wizardDevice = data.wizardDevice ?? null;
            this.wizardAssetType = data.wizardAssetType ?? null;
            this._deviceCheckDone = false;
            this._deviceCount = null;
        },

        dismissWizard() {
            this.wizardDismissed = true;
            this._persist();
        },
        markSetupComplete() {
            this.hasCompletedSetup = true;
            this._persist();
        },

        markDeviceActivated() {
            this.hasActivatedDevice = true;
            this._persist();
        },

        markAssetCreated() {
            this.hasCreatedAsset = true;
            this._persist();
        },

        markLiveMapViewed() {
            if (!this.hasViewedLiveMap) {
                this.hasViewedLiveMap = true;
                this._persist();
            }
        },

        markTripsViewed() {
            if (!this.hasViewedTrips) {
                this.hasViewedTrips = true;
                this._persist();
            }
        },

        markDriverAdded() {
            if (!this.hasAddedDriver) {
                this.hasAddedDriver = true;
                this._persist();
            }
        },

        markLocationAdded() {
            if (!this.hasAddedLocation) {
                this.hasAddedLocation = true;
                this._persist();
            }
        },

        dismissChecklist() {
            this.checklistDismissed = true;
            this._persist();
        },

        setDeviceCheckResult(count) {
            this._deviceCheckDone = true;
            this._deviceCount = count;
        },

        setWizardStep(step) {
            this.wizardStep = step;
            this._persist();
        },

        setWizardDevice(device) {
            this.wizardDevice = device;
            this._persist();
        },

        setWizardAssetType(assetType) {
            this.wizardAssetType = assetType;
            this._persist();
        },

        completeWizard() {
            this.hasCompletedSetup = true;
            this.wizardStep = null;
            this.wizardDevice = null;
            this.wizardAssetType = null;
            // Reset transient device check so router guard doesn't use stale count
            this._deviceCheckDone = false;
            this._deviceCount = null;
            this._persist();
        },

        get needsWizard() {
            return this._deviceCheckDone && this._deviceCount === 0 && !this.hasCompletedSetup;
        },

        detectExistingData(deviceCount, assetCount, driverCount = 0, locationCount = 0) {
            if (deviceCount > 0) this.hasActivatedDevice = true;
            if (assetCount > 0) this.hasCreatedAsset = true;
            if (driverCount > 0) this.hasAddedDriver = true;
            if (locationCount > 0) this.hasAddedLocation = true;
            // If all data milestones are met, auto-complete view milestones and dismiss -
            // this is clearly an existing user who doesn't need the getting-started checklist
            if (this.hasActivatedDevice && this.hasCreatedAsset && this.hasAddedDriver && this.hasAddedLocation) {
                this.hasViewedLiveMap = true;
                this.hasViewedTrips = true;
                this.checklistDismissed = true;
            }
            this._persist();
        },

        reset() {
            this.hasCompletedSetup = false;
            this.checklistDismissed = false;
            this.hasActivatedDevice = false;
            this.hasCreatedAsset = false;
            this.hasViewedLiveMap = false;
            this.hasViewedTrips = false;
            this.hasAddedDriver = false;
            this.hasAddedLocation = false;
            this.wizardStep = null;
            this.wizardDevice = null;
            this.wizardAssetType = null;
            this.wizardDismissed = false;
            this._deviceCheckDone = false;
            this._deviceCount = null;
            // In memory only. The signed out user's entry stays on disk so
            // their progress, and their Skip for now, survive the next sign in.
            activeKey = STORAGE_KEY;
        },
    },
});
